import { isConversationProjectionItem, useContentDetail, useContentList, useToggleRead } from '@/hooks';
import { useContentDisplayTitle } from '@/hooks/content';
import { useSpeak2MeStore } from '@/store/speak2meStore';
import { formatDate } from '@/utils/dateUtils';
import type { Content } from '@vivport/backend/types';

export function ChatUnreadList() {
  const query = useContentList({
    contentTypes: ['NEO_CHAT'],
    limit: 100,
    orderBy: 'timelineAt',
    orderDir: 'desc',
  });
  const ids = (query.data?.items ?? [])
    .filter((item) => !isConversationProjectionItem(item) && item.tracking?.isRead !== true)
    .map((item) => item.id);
  const showLoading = query.isLoading && ids.length === 0;
  const showEmpty = !showLoading && ids.length === 0;

  return (
    <div className="flex-1 overflow-y-auto scrollbar-thin">
      {showLoading && <p className="text-sm text-muted-foreground p-4">Loading…</p>}
      {showEmpty && (
        <p className="text-sm text-muted-foreground p-4">
          All caught up — no NeoChats need attention.
        </p>
      )}
      {!showLoading && !showEmpty && (
        <ul className="divide-y">
          {ids.map((id) => (
            <ChatUnreadListRow key={id} contentId={id} />
          ))}
        </ul>
      )}
    </div>
  );
}

function ChatUnreadListRow({ contentId }: { contentId: number }) {
  const { data: content } = useContentDetail(contentId);
  if (!content) {
    return null;
  }
  return <ChatUnreadListRowBody content={content} />;
}

function ChatUnreadListRowBody({ content }: { content: Content }) {
  const setView = useSpeak2MeStore((s) => s.setView);
  const setResumeNeoChatId = useSpeak2MeStore((s) => s.setResumeNeoChatId);
  const toggleReadMutation = useToggleRead();
  const displayTitle = useContentDisplayTitle(content);

  const handleClick = () => {
    setView('chat');
    setResumeNeoChatId(content.id);
    toggleReadMutation.mutate({ contentId: content.id, isRead: true });
  };

  const date = new Date(content.timelineAt || content.originCreatedAt);
  return (
    <li>
      <button
        type="button"
        onClick={handleClick}
        className="w-full text-left px-4 py-3 hover:bg-muted/40 transition-colors"
      >
        <p className="text-sm font-medium text-foreground truncate">{displayTitle}</p>
        <p className="text-xs text-muted-foreground">{formatDate(date)}</p>
      </button>
    </li>
  );
}
